import qs from 'qs';

import type { AsyncDataReloaderT } from '@dr.pogodin/react-global-state';
import { assertEmptyObject, time } from '@dr.pogodin/react-utils';

import { type GetVideosOptionsT, listVideos } from 'services/video';

import {
  type BodyAreaT,
  DEFAULT_VIDEO_PAGE_SIZE,
  type DifficultyT,
  type FilterT,
  type ModalityT,
  type VideoIntentT,
  type VideoListT,
  type VideoSortT,
  type VideoT,
  filterToArray,
  getId,
  useAsyncData,
} from 'utils';

export type QueryT = {
  artistIds?: FilterT<string>;
  asEndUser?: boolean;
  bodyAreas?: FilterT<BodyAreaT>;
  customModalities?: string[];
  difficulty?: FilterT<DifficultyT>;
  durationMin?: [number, number];
  intents?: FilterT<VideoIntentT>;
  modalities?: FilterT<ModalityT>;
  textSearch?: string;
};

function mapQuery(
  page: number | undefined,
  pageSize: number | undefined,
  {
    artistIds,
    asEndUser,
    bodyAreas,
    customModalities,
    difficulty,
    durationMin,
    intents,
    modalities,
    textSearch,
    ...rest
  }: QueryT = {},
  sort?: VideoSortT,
): GetVideosOptionsT {
  assertEmptyObject(rest);

  return {
    artistIds: filterToArray(artistIds),
    asEndUser,
    bodyAreas: filterToArray(bodyAreas),
    customModalities,
    difficulty: filterToArray(difficulty),
    durationMin,

    // NOTE: As of now, the backend does not support the counting of total
    // number of videos a the end user; and we don't need it (in theory),
    // because end users rely on progressive loading of video pages, thus
    // don't need the total count (which is used for pagination). I guess,
    // somewhere on the way we changed how the progressive loading works,
    // and now it relies on the same `page` value which used to mean we use
    // the non-progressive loading... no need to revise it now, I guess,
    // let's just explicitly opt-out of the count if `asEndUser` is true.
    getTotalCount: page !== undefined && !asEndUser,

    intents: filterToArray(intents),
    modalities: filterToArray(modalities),
    page,
    pageSize,
    sort,
    textSearch,
  };
}

type UseVideosOptionsT = {
  disabled?: boolean;
  page?: number;
  pageSize?: number;
  query?: QueryT;
  sort?: VideoSortT;

  // TODO: Move inside the query?
  textSearch?: string;
};

type UseVideosResT = {
  items: VideoT[];
  loadMore: (() => void) | undefined;
  loading: boolean;
  numPages: number | undefined;
  reload: AsyncDataReloaderT<VideoListT>;
};

export function useVideos({
  disabled,
  page,
  pageSize = DEFAULT_VIDEO_PAGE_SIZE,
  query,
  sort,
}: UseVideosOptionsT): UseVideosResT {
  if (page && page < 0) throw Error(`"page" must be non-negative (got ${page})`);

  let queryString: string = qs.stringify(query);
  if (sort) queryString += `/${qs.stringify(sort)}`;

  // NOTE: The following loader always loads a single page of results (the first
  // page, in the case of progressive loading, where `page` option is "undefined").
  // For loading follow-up pages in the case of progressive loading a dedicated
  // loader is defined further below, in the end of this hook's code.
  const e = useAsyncData('videos', async (old): Promise<VideoListT> => {
    if (
      old && (page === undefined || (
        page === old.lastPage
        && old.items.length === old.pageSize
      )) && pageSize === old.pageSize && queryString === old.queryString
    ) return old;

    return (async (): Promise<VideoListT> => {
      const res = await listVideos(mapQuery(page, pageSize, query, sort));

      return {
        hasMore: res.videos.length === pageSize,
        items: res.videos,
        lastPage: page ?? 0,
        numPages: res.totalCount && Math.ceil(res.totalCount / pageSize),
        pageSize,
        queryString,
      };
    })();
  }, {
    deps: [page, pageSize, queryString],
    disabled,
    maxage: time.YEAR_MS,
  });

  return {
    items: e.data?.items ?? [],
    loadMore: e.data?.hasMore && !e.loading ? () => {
      void e.reload(async (old): Promise<VideoListT> => {
        if (!old) throw Error('Internal error');

        const res = await listVideos(
          mapQuery(old.lastPage + 1, pageSize, query),
        );

        const items = [...old.items];
        const itemMap: Record<string, number> = {};
        for (let idx = 0; idx < items.length; ++idx) {
          itemMap[getId(items[idx])] = idx;
        }

        for (const video of res.videos) {
          const videoId = getId(video);
          const idx = itemMap[videoId];
          if (idx === undefined) items.push(video);
          else items[idx] = video;
        }

        return {
          hasMore: res.videos.length === pageSize,
          items,
          lastPage: old.lastPage + 1,
          numPages: res.totalCount && Math.ceil(res.totalCount / pageSize),
          pageSize,
          queryString,
        };
      });
    } : undefined,
    loading: e.loading,
    numPages: e.data?.numPages,
    reload: e.reload,
  };
}
